import React, { useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY } from "../theme.js";
import { supabase, friendlyError } from "../lib/supabase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { LogoMark, Wordmark, Field, ErrorNote, Spinner } from "../components/ui.jsx";
import { inputStyle, primaryBtnStyle, ghostBtnStyle, btnState } from "../components/styles.js";

/* Where a password-reset link lands.
   A reset link signs you in for real — Supabase issues an ordinary session —
   so nothing stops the app from simply showing the Passport, which is what it
   used to do. The collector clicked "reset my password", got dropped into the
   app, and had no way to set one. This screen is the interception. */
export default function SetNewPassword() {
  const { user, endRecovery, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const longEnough = password.length >= 6;
  const passwordsMatch = password === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = longEnough && passwordsMatch && confirmPassword.length > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      /* The session is already valid and stays valid, so there is no second
         sign-in to do: lowering the flag drops them into their Passport. */
      endRecovery();
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: `linear-gradient(180deg, rgba(10,10,10,0.2), ${COLORS.black} 70%), linear-gradient(135deg, #1a1611, #0A0A0A)`,
        padding: "24px 24px 40px",
      }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, minHeight: 180, paddingBottom: 18 }}>
        <LogoMark size={64} />
        <Wordmark size={22} />
      </div>

      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 18, color: COLORS.cream }}>Choose a new password</div>
        <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 3 }}>
          {user?.email ? `for ${user.email}` : "Your reset link is verified"}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12 }}>
          <ErrorNote message={error} />
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="New password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            autoFocus
            style={inputStyle}
          />
          {password.length > 0 && !longEnough && (
            <span style={{ fontSize: 11.5, fontFamily: FONT_BODY, color: COLORS.creamFaint }}>
              Password must be at least 6 characters.
            </span>
          )}
        </Field>

        <Field label="Confirm new password">
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Type it again"
            autoComplete="new-password"
            style={{ ...inputStyle, borderColor: showMismatch ? COLORS.red : COLORS.line }}
          />
          {showMismatch && (
            <span style={{ fontSize: 11.5, fontFamily: FONT_BODY, color: COLORS.red }}>
              Passwords don't match yet.
            </span>
          )}
        </Field>

        <button
          type="submit"
          disabled={!canSubmit || busy}
          style={{ ...btnState(primaryBtnStyle, canSubmit && !busy), display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
        >
          {busy && <Spinner size={14} color="#1A1408" />}
          Save new password
        </button>
      </form>

      {/* A reset link can be clicked by someone who no longer wants to change
          anything. Signing out is the honest way off this screen — skipping
          straight into the app would leave a session opened by an emailed
          link that its owner decided not to use. */}
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <button type="button" onClick={signOut} disabled={busy} style={{ ...ghostBtnStyle, fontSize: 12.5, color: COLORS.goldBright }}>
          Cancel and sign out
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: 22, fontSize: 10, fontFamily: FONT_BODY, color: COLORS.creamFaint, lineHeight: 1.6 }}>
        Didn't ask for this? Sign out above and your password stays exactly as it was.
      </div>
    </div>
  );
}
